import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, ChevronDown, ChevronUp, MessageSquare, Quote } from 'lucide-react';

const CLASSIFICATION_CONFIG = {
  success: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    label: 'Success',
    icon: CheckCircle2,
  },
  violation: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-700',
    dot: 'bg-rose-500',
    label: 'Violation',
    icon: AlertTriangle,
  },
  hesitation: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
    label: 'Hesitation',
    icon: AlertCircle,
  },
};

/* ─── Single Moment Card ─── */
const MomentCard = ({ moment, index }) => {
  const config = CLASSIFICATION_CONFIG[moment.classification] || CLASSIFICATION_CONFIG.hesitation;
  const Icon = config.icon;

  return (
    <div
      className={`${config.bg} border ${config.border} rounded-xl p-3 transition-all hover:shadow-sm`}
      style={{ animationDelay: `${index * 80}ms`, animation: 'fadeSlideIn 0.4s ease-out backwards' }}
      data-testid={`moment-${index}`}
    >
      <div className="flex items-start gap-2.5">
        <div className={`w-6 h-6 rounded-lg ${config.bg} border ${config.border} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon size={12} className={config.text} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${config.text}`}>{config.label}</span>
            {moment.speaker && (
              <span className="text-[10px] text-slate-400">{moment.speaker}</span>
            )}
          </div>
          <p className={`text-xs leading-relaxed ${config.text} font-medium`}>
            "{moment.text}"
          </p>
          {moment.reason && (
            <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed italic">
              {moment.reason}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Moment of Truth Transcript View ─── */
export const MomentOfTruth = ({ moments, title }) => {
  const [expanded, setExpanded] = useState(false);

  if (!moments || moments.length === 0) return null;

  const successCount = moments.filter(m => m.classification === 'success').length;
  const violationCount = moments.filter(m => m.classification === 'violation').length;
  const hesitationCount = moments.filter(m => m.classification === 'hesitation').length;
  const visibleMoments = expanded ? moments : moments.slice(0, 5);

  return (
    <div data-testid="moment-of-truth">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{title || 'Moment of Truth Analysis'}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Color-coded transcript highlights</p>
        </div>
        <div className="flex items-center gap-2">
          {successCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              <CheckCircle2 size={9} /> {successCount}
            </span>
          )}
          {violationCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
              <AlertTriangle size={9} /> {violationCount}
            </span>
          )}
          {hesitationCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
              <AlertCircle size={9} /> {hesitationCount}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {visibleMoments.map((moment, i) => (
          <MomentCard key={i} moment={moment} index={i} />
        ))}
      </div>

      {moments.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 w-full text-center text-xs font-semibold text-slate-500 hover:text-slate-700 py-2 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-center gap-1 transition-colors"
          data-testid="moment-expand-toggle"
        >
          {expanded ? <><ChevronUp size={12} /> Show Less</> : <><ChevronDown size={12} /> Show All {moments.length} Moments</>}
        </button>
      )}
    </div>
  );
};

/* ─── AI Attribution / Score Reasoning ─── */
export const AIAttribution = ({ reasoning, scores, auditScores, roleQuestionLabels }) => {
  const [expanded, setExpanded] = useState(false);

  if (!reasoning && !scores && !auditScores) return null;

  const reasoningEntries = [];

  // Build reasoning entries from score_reasoning
  if (reasoning) {
    const labels = {
      quality_reasoning: { label: 'Quality (Q)', color: 'sky', weight: '40%' },
      compliance_reasoning: { label: 'Compliance (C)', color: 'emerald', weight: '30%' },
      sentiment_reasoning: { label: 'Sentiment (S)', color: 'violet', weight: '20%' },
      dynamics_reasoning: { label: 'Dynamics (D)', color: 'amber', weight: '10%' },
      overall_reasoning: { label: 'Overall Grade', color: 'slate', weight: '' },
    };
    for (const [key, config] of Object.entries(labels)) {
      if (reasoning[key]) {
        reasoningEntries.push({ ...config, text: reasoning[key], key });
      }
    }
  }

  // Build reasoning from audit_scores evidence — supports both old (q1_total_cost) and new (q1) key formats
  if (auditScores) {
    // Default admissions advisor labels (legacy long-form keys)
    const defaultLabels = {
      q1_total_cost: 'Q1: Total Cost', q2_osap_eligibility: 'Q2: OSAP/Aid',
      q3_prerequisites: 'Q3: Prerequisites', q4_job_placement: 'Q4: Placement',
      q5_campus_tour: 'Q5: Campus Tour',
    };
    // Legacy long-form keys map to the same short (q1-q5) keys role_question_labels is keyed by
    const LEGACY_KEY_TO_SHORT = {
      q1_total_cost: 'q1', q2_osap_eligibility: 'q2', q3_prerequisites: 'q3',
      q4_job_placement: 'q4', q5_campus_tour: 'q5',
    };
    // Role-specific labels from the analysis (q1, q2, q3, q4, q5 keys)
    const rql = roleQuestionLabels || {};

    for (const [key, data] of Object.entries(auditScores)) {
      if (key === 'composite_audit_score') continue;
      if (data && typeof data === 'object' && data.evidence && data.evidence !== 'NOT ADDRESSED') {
        const label = data.label || rql[LEGACY_KEY_TO_SHORT[key] || key] || defaultLabels[key] || key.toUpperCase();
        reasoningEntries.push({
          label,
          color: data.score >= 0.8 ? 'emerald' : data.score >= 0.5 ? 'amber' : 'rose',
          weight: `${data.score?.toFixed(1)}/1.0`,
          text: data.evidence,
          key,
        });
      }
    }
  }

  if (reasoningEntries.length === 0) return null;

  const visibleEntries = expanded ? reasoningEntries : reasoningEntries.slice(0, 3);

  const colorMap = {
    sky: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', badge: 'bg-sky-100 text-sky-800' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
    violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-800' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
    slate: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-800' },
    rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-800' },
  };

  return (
    <div data-testid="ai-attribution">
      <div className="flex items-center gap-2 mb-3">
        <Quote size={12} className="text-indigo-500" />
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">AI Attribution — Score Reasoning</p>
      </div>

      <div className="space-y-2">
        {visibleEntries.map((entry, i) => {
          const c = colorMap[entry.color] || colorMap.slate;
          return (
            <div
              key={entry.key}
              className={`${c.bg} border ${c.border} rounded-xl p-3`}
              style={{ animationDelay: `${i * 100}ms`, animation: 'fadeSlideIn 0.4s ease-out backwards' }}
              data-testid={`attribution-${entry.key}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-[10px] font-bold ${c.badge} px-2 py-0.5 rounded-md`}>{entry.label}</span>
                {entry.weight && (
                  <span className="text-[10px] font-bold text-slate-400">{entry.weight}</span>
                )}
              </div>
              <p className={`text-xs leading-relaxed ${c.text}`}>{entry.text}</p>
            </div>
          );
        })}
      </div>

      {reasoningEntries.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 w-full text-center text-xs font-semibold text-slate-500 hover:text-slate-700 py-2 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-center gap-1 transition-colors"
          data-testid="attribution-expand-toggle"
        >
          {expanded ? <><ChevronUp size={12} /> Show Less</> : <><ChevronDown size={12} /> Show All {reasoningEntries.length} Reasoning Blocks</>}
        </button>
      )}
    </div>
  );
};
